export default angular.module('eventix.common.services.shopAnalytics', [])
    .factory('ShopAnalytics', ShopAnalytics).name;

function ShopAnalytics(OrderManager, $analytics) {
    /**
     * @ngdoc service
     * @name eventix.common.services.ShopAnalytics
     * @description
     * A service for e-commerce analytics tracking
     */
    const service = {
        get order() { return OrderManager.instance; },
        /**
         * @ngdoc method
         * @name eventix.common.services.ShopAnalytics#impression
         * @methodOf eventix.common.services.ShopAnalytics
         * @description Log impression of tickets
         * @param {Array<Ticket>} [tickets] Optional array of tickets if we're only viewing a subselection
         */
        impression(tickets) {
            if(!tickets)
                tickets = _.flatten(_.values(service.order.tickets));
            _.filter(tickets, { available: true });
            let impressionArray = _.map(tickets, function(ticket){
                return {
                    id: _.get(ticket, '0.guid'),
                    name: _.get(ticket, '0.name'),
                    price: parseFloat(_.get(ticket, '0.minimumPrice') / 100).toFixed(2),
                    list: service.order.shop.name
                };
            });
            $analytics.eventTrack('ecommerce', {
                event: 'impression',
                ecommerce: {
                    impressions: impressionArray
                }
            });
        },
        /**
         * @ngdoc method
         * @name eventix.common.services.ShopAnalytics#addToCart
         * @methodOf eventix.common.services.ShopAnalytics
         * @description Log adding ticket to cart
         * @param {Ticket} ticket The ticket being added
         */
        addToCart(ticket) {
            $analytics.eventTrack('ecommerce', {
                event: 'addToCart',
                ecommerce: {
                    add: {
                        products: [{
                            id: _.get(ticket, 'guid'),
                            name: _.get(ticket, 'name'),
                            price: parseFloat(_.get(ticket, 'minimumPrice') / 100).toFixed(2),
                            quantity: 1
                        }]
                    }
                }
            });
        },
        /**
         * @ngdoc method
         * @name eventix.common.services.ShopAnalytics#removeFromCart
         * @methodOf eventix.common.services.ShopAnalytics
         * @description Log removing ticket from cart
         * @param {Ticket} ticket The ticket being removed
         */
        removeFromCart(ticket) {
            $analytics.eventTrack('ecommerce', {
                'event': 'removeFromCart',
                'ecommerce': {
                    'remove': {
                        'products': [{
                            'id': _.get(ticket, 'guid'),
                            'name': _.get(ticket, 'name'),
                            'price': parseFloat(_.get(ticket, 'minimumPrice') / 100).toFixed(2),
                            'quantity': 1
                        }]
                    }
                }
            });
        },
        /**
         * @ngdoc method
         * @name eventix.common.services.ShopAnalytics#checkoutStepOne
         * @methodOf eventix.common.services.ShopAnalytics
         * @description Log the first step of checkout process, filling out personal details
         */
        checkoutStepOne() {
            let orderTickets = [];
            _.forEach(service.order.reservations, function(reservations){
                _.forEach(reservations, function(ticketReservation){
                    let ticket = ticketReservation.ticket;
                    if(!ticket) return;
                    orderTickets.push({
                        id: ticket.guid,
                        name: ticket.name,
                        price: parseFloat(ticket.minimumPrice / 100).toFixed(2),
                        quantity: 1,
                        list: service.order.shop.name
                    });
                });
            });
            // To complete the Flow, show tickets as Detail
            $analytics.eventTrack('ecommerce', {
                event: 'detail',
                ecommerce: {
                    detail: {
                        actionField: { list: service.order.shop.name },
                        products: orderTickets
                    }
                }
            });
            // Start Checkout
            $analytics.eventTrack('ecommerce', {
                event: 'checkout',
                ecommerce: {
                    checkout: {
                        actionField: { step: 1, list: service.order.shop.name },
                        products: orderTickets
                    }
                }
            });

        },
        /**
         * @ngdoc method
         * @name eventix.common.services.ShopAnalytics#checkoutStepOne
         * @methodOf eventix.common.services.ShopAnalytics
         * @description Log the second step of checkout process, selecting a payment method
         */
        checkoutStepTwo() {
            let orderTickets = [];
            _.forEach(service.order.reservations, function(reservations) {
                _.forEach(reservations, function(ticketReservation) {
                    let ticket = ticketReservation.ticket;
                    if(!ticket) return;
                    orderTickets.push({
                        id: ticket.guid,
                        name: ticket.name,
                        price: parseFloat(ticketReservation.price / 100).toFixed(2),
                        quantity: 1
                    });
                });
            });
            $analytics.eventTrack('ecommerce', {
                event: 'checkout',
                ecommerce: {
                    checkout: {
                        actionField: { step: 2, list: service.order.shop.name },
                        products: orderTickets
                    }
                }
            });
        },
        /**
         * @ngdoc method
         * @name eventix.common.services.ShopAnalytics#conversion
         * @methodOf eventix.common.services.ShopAnalytics
         * @description Log the conversion of events and tickets.
         * Call this on the order status page, after the visitor returns from the payment
         * @param {Order} order The order object from API
         */
        conversion(order) {
            let totalPaid = _.reduce(order.payments, (total, p) => p.status === 'paid' ? total + p.amount : total, 0);
            $analytics.eventTrack(null, {
                category: 'OrderProcess',
                action: 'Completed order',
                label: 'Payment: ' + _.get(_.last(order.payments), 'status', 'unknown'),
                value: parseFloat(totalPaid / 100).toFixed(2)
            });

            if (_.get(order.status, 'cancelled') === 'cancelled')
                return;

            // Custom events
            var products = _.flatMap(order.tickets, ticket => _.map(ticket.products, product => product.product));
            var eventDates = _.flatMap(products, product => product.event_dates);
            var events = _.uniqBy(_.map(eventDates, 'event'), 'guid');
            var locations = _.transform(eventDates, (resultSet, date) => {
                if (!date.location)
                    return;
                let id = [date.guid, date.location.guid].join('_');
                let coords = [date.location.latitude, date.location.longitude].join(',');
                if (!/-?\d{1,2}\.\d+,-?\d{1,3}\.\d+/.test(coords))
                    coords = null;
                if ((!coords && _.isEmpty(date.location.address)) || resultSet[id])
                    return;
                resultSet[id] = {
                    id: id,
                    when: [date.start.slice(0, 10), date.end.slice(0, 10)].join('_'),
                    where: date.location.address,
                    coords: coords
                };
            }, {});
            _.forEach(_.get(order, 'conversions.events'), event => {
                if (!angular.isObject(event))
                    return;
                if (_.get(order.status, 'cancelled') === 'cancelled')
                    return;
                $analytics.eventTrack(null, {
                    category: 'EventConversion',
                    action: 'Completed order for event',
                    value: parseFloat(event.total / 100).toFixed(2),
                    label: event.guid + '|-|' + event.name
                });
            });
            _.forEach(locations, location => {
                $analytics.eventTrack(null, {
                    category: 'LocationAttend',
                    action: location.coords,
                    value: location.when,
                    label: location.where
                });
            });

            // Analytics E-Commerce events
            let currentTickets = [];
            _.forEach(order.tickets, function(orderTicket){
                currentTickets.push({
                    id: _.get(orderTicket.ticket, 'guid'),
                    name: _.get(orderTicket.ticket, 'name'),
                    price: parseFloat(_.get(orderTicket, 'price') / 100).toFixed(2),
                    coupon: _.get(orderTicket, 'coupon', ''),
                    quantity: 1
                });
            });
            const conversions = _.cloneDeep(_.get(order, 'conversions', {}));
            conversions.total = parseFloat((conversions.total / 100) || 0).toFixed(2);
            _.forEach(conversions.events, (conversion) => {
                conversion.total = parseFloat((conversion.total / 100) || 0).toFixed(2);
                _.forEach(conversion.tickets, (ticket) => {
                    ticket.amount = parseFloat((ticket.amount / 100) || 0).toFixed(2);
                });
            });
            // Push Order id to DataLayer
            $analytics.eventTrack('ecommerce', {
                order: order,
                orderId: _.get(order, 'guid'),
                orderConverions: conversions
            });
            $analytics.eventTrack('ecommerce', {
                event: 'purchase',
                value: parseFloat(totalPaid / 100).toFixed(2),
                ecommerce: {
                    purchase: {
                        'actionField': {
                            id: order.guid,
                            affiliation: order.shop.name,
                            revenue: parseFloat(totalPaid / 100).toFixed(2),
                            step: 2
                        },
                        products: currentTickets
                    }
                }
            });
        }
    };
    return service;
}
